import { gql } from '@apollo/client';

/**
 * Trigger 1 writes one perception_model_product_lookup row per product per job.
 * Everything here keys on perceptionModelJobId, never versionId: a version is
 * not unique (a failed run and its successful re-run share one), and filtering
 * the export by version would span jobs and blend duplicate peer lists.
 */

/** [productName, distance] as stored in the peers JSONB array. */
export type PeerEntry = [string, number];

export type ProductLookupRow = {
  id: number;
  productName: string;
  perceptionModelJobId: number;
  /** { sugar: { intensity, hurdle }, ... } */
  ggvars: Record<string, { intensity?: number; hurdle?: number }> | null;
  /** { token: prevalence }, only entries above the export threshold */
  tokensFlavor: Record<string, number> | null;
  tokensTexture: Record<string, number> | null;
  tokensSensation: Record<string, number> | null;
  peers: PeerEntry[] | null;
  /** gastrograph.product ids sharing this name — names are not unique */
  productIds: number[] | null;
};

export type NormSetPeer = { product: string; mean_dist: number | null };

/**
 * A saved norm set. The backing table is gastrograph.perception_model_project —
 * "project" and "norm set" name the same thing. The UI says "norm set" because
 * the console already has an unrelated Projects section.
 */
export type ProjectNormSetRow = {
  id: number;
  name: string;
  perceptionModelJobId: number;
  /** owning workspace: norm sets are per-producer, not global */
  producerId: number;
  anchors: string[];
  normSet: NormSetPeer[];
  createdDate: string;
};

/** A selectable perception model, with the size of its exported universe. */
export type PerceptionModelJobOption = {
  id: number;
  versionId: string;
  universe: string;
  isDeployed: boolean;
  /** the date reviews were pulled: anything reviewed after this is not in it */
  pullDate: string;
  /** run metadata; carries demo_levels, the only record of trained levels */
  params: { demo_levels?: Record<string, string[]> } | null;
  /**
   * SCREAMING_SNAKE from report_status_enum. Needed on this document so the
   * list can show a STARTED run rather than pretending it does not exist.
   */
  status?: string;
  /** only selected by PerceptionModelJobByIdQuery, hence optional here */
  statusReason?: string | null;
  startedAt?: string;
  finishedAt?: string | null;
  perceptionModelProductLookupsByPerceptionModelJobId: { totalCount: number };
};

/**
 * Every perception model, newest first, with how many products each one
 * exported.
 *
 * The count is what makes a job usable: a run that failed or is still going has
 * no lookup rows, so it can neither anchor nor rank anything. It comes from the
 * FK on perception_model_product_lookup, so it is one query rather than one per
 * job.
 */
export const PerceptionModelJobsQuery = gql`
  query PerceptionModelJobsQuery {
    allPerceptionModelJobs(orderBy: STARTED_AT_DESC) {
      nodes {
        id
        versionId
        universe
        isDeployed
        pullDate
        params
        status
        perceptionModelProductLookupsByPerceptionModelJobId {
          totalCount
        }
      }
    }
  }
`;

/**
 * One training run as the history table needs it.
 *
 * `status` arrives SCREAMING_SNAKE from the `report_status_enum` column
 * (STARTED, IN_PROGRESS, SUCCESS, FAILED, REQUESTED), matching the convention
 * already in `STATUS_ENUM` for report jobs.
 */
export type PerceptionModelJobRun = {
  id: number;
  versionId: string;
  universe: string;
  pullDate: string;
  status: string;
  statusReason: string | null;
  startedAt: string;
  finishedAt: string | null;
  isDeployed: boolean;
};

/**
 * Run history for the training launcher.
 *
 * Deliberately a separate document rather than extra fields on
 * PerceptionModelJobsQuery: that one is mocked with fixed fixtures by three
 * spec files, and widening it would leave them returning rows missing the new
 * fields. It also carries no product count, which is the expensive part of the
 * other query and meaningless for a run that has not finished.
 */
export const PerceptionModelJobHistoryQuery = gql`
  query PerceptionModelJobHistoryQuery($first: Int) {
    allPerceptionModelJobs(orderBy: STARTED_AT_DESC, first: $first) {
      nodes {
        id
        versionId
        universe
        pullDate
        status
        statusReason
        startedAt
        finishedAt
        isDeployed
      }
    }
  }
`;

/**
 * Anchor rows, pinned to one job.
 *
 * The only place `peers` is needed: `rankCandidates` merges the anchors' peer
 * lists to produce the candidate ranking. At 8.2 kB a row that field is by far
 * the heaviest in the table, so nothing else selects it.
 */
export const AnchorLookupQuery = gql`
  query AnchorLookupQuery($jobId: Int!, $names: [String!]) {
    allPerceptionModelProductLookups(
      filter: {
        perceptionModelJobId: { equalTo: $jobId }
        productName: { in: $names }
      }
    ) {
      nodes {
        id
        productName
        perceptionModelJobId
        ggvars
        tokensFlavor
        tokensTexture
        tokensSensation
        peers
        productIds
      }
    }
  }
`;

/**
 * Rows for products being displayed: ranked candidates, or one clicked point.
 *
 * Deliberately without `peers`. Nothing on this path reads it —
 * `distanceToAnchors` takes a product name and scores it from `anchorRows`
 * alone — and at top-N 200 it was 1.6 MB of dead weight per ranking.
 */
export const ProductLookupByNamesQuery = gql`
  query ProductLookupByNamesQuery($jobId: Int!, $names: [String!]) {
    allPerceptionModelProductLookups(
      filter: {
        perceptionModelJobId: { equalTo: $jobId }
        productName: { in: $names }
      }
    ) {
      nodes {
        id
        productName
        perceptionModelJobId
        ggvars
        tokensFlavor
        tokensTexture
        tokensSensation
        productIds
      }
    }
  }
`;

/**
 * Saved norm sets for one workspace, newest first. Drives the sidebar subtree.
 *
 * Scoped by producerId: the table carries no row level security, so the filter
 * is the only thing keeping one workspace's norm sets out of another's list.
 */
export type NormSetListData = {
  allPerceptionModelProjects: { nodes: ProjectNormSetRow[] } | null;
};
export const AllProjectNormSetsQuery = gql`
  query AllProjectNormSetsQuery($producerId: Int!) {
    allPerceptionModelProjects(
      condition: { producerId: $producerId }
      orderBy: CREATED_DATE_DESC
    ) {
      nodes {
        id
        name
        perceptionModelJobId
        producerId
        anchors
        normSet
        createdDate
      }
    }
  }
`;

/**
 * One saved norm set. Drives the picker at /perception-models/:normSetId.
 *
 * Keyed on the client as well as the id, rather than a plain by-primary-key
 * lookup: the id comes off the URL, so without the producerId term anyone
 * could read another workspace's norm set by typing its number. There is no
 * row level security on this table, so this condition is the check. A norm set
 * belonging to another client returns no rows and renders as missing, which is
 * what it is from that workspace's point of view.
 */
export const ProjectNormSetByIdQuery = gql`
  query ProjectNormSetByIdQuery($id: Int!, $producerId: Int!) {
    allPerceptionModelProjects(
      condition: { id: $id, producerId: $producerId }
    ) {
      nodes {
        id
        name
        perceptionModelJobId
        producerId
        anchors
        normSet
        createdDate
      }
    }
  }
`;

/**
 * Which products this perception model actually knows, for the job in play.
 *
 * The anchor list is the intersection of this and the workspace's products.
 * There is no FK between gastrograph.product and the lookup table — the link is
 * the product_ids array — so the join cannot be expressed in PostGraphile and
 * is done in the browser. This is the small side of it: one row per modelled
 * product, against workspaces that run to thousands of products.
 */
export const ModelledProductIndexQuery = gql`
  query ModelledProductIndexQuery($jobId: Int!) {
    allPerceptionModelProductLookups(
      condition: { perceptionModelJobId: $jobId }
    ) {
      nodes {
        id
        productName
        productIds
      }
    }
  }
`;

/** One point on the map. Deliberately no tokens — see ExplorerIndexQuery. */
export type ExplorerIndexRow = {
  id: number;
  productName: string;
  /** UMAP coordinates: relative position only, axes carry no meaning */
  umap1: number | null;
  umap2: number | null;
};

/** One product carrying one token, from the perception_model_token view. */
export type TokenRow = {
  perceptionModelJobId: number;
  kind: string;
  token: string;
  productName: string;
};

/**
 * Where every product sits on the map, for one job.
 *
 * Name and coordinates only. The token maps used to ride along here and were
 * 21 MB of a 22 MB response on job 5 (16,356 rows) — normalised into 16k Apollo
 * nodes, that is a few hundred MB of heap for data the map never draws. Tokens
 * now stay in the database, where filtering them is a 39 ms query.
 *
 * At ~1.1 MB for a 16k-product universe this is affordable whole; if one ever
 * reaches six figures it needs paging or a bounding box.
 */
export const ExplorerIndexQuery = gql`
  query ExplorerIndexQuery($jobId: Int!) {
    allPerceptionModelProductLookups(
      condition: { perceptionModelJobId: $jobId }
    ) {
      nodes {
        id
        productName
        umap1
        umap2
      }
    }
  }
`;

/** A demographic rendering of a norm set. Child of perception_model_project. */
export type TranslationRow = {
  id: number;
  name: string;
  perceptionModelProjectId: number;
  /** product names to render */
  products: string[];
  /**
   * One key per dimension, each either { level: weight } summing to 1, or the
   * string "typical". Mirrors build_demo_grid's own parse_dim contract.
   */
  demographic: Record<string, Record<string, number> | 'typical'>;
  createdAt: string;
  /**
   * SCREAMING_SNAKE from report_status_enum, the same as a perception model
   * job: STARTED, IN_PROGRESS, SUCCESS, FAILED. STARTED means the audience is
   * defined and nothing has been asked of it yet, which is what makes it
   * eligible to render.
   */
  status: string;
  statusReason: string | null;
  /** when the render began, not when the row was made */
  startedAt: string | null;
  finishedAt: string | null;
};

export type TranslationListData = {
  allPerceptionModelProjectTranslations: { nodes: TranslationRow[] } | null;
};

/** Translations belonging to one norm set, newest first. */
export const AllProjectTranslationsQuery = gql`
  query AllProjectTranslationsQuery($projectId: Int!) {
    allPerceptionModelProjectTranslations(
      condition: { perceptionModelProjectId: $projectId }
      orderBy: CREATED_AT_DESC
    ) {
      nodes {
        id
        name
        perceptionModelProjectId
        products
        demographic
        createdAt
        status
        statusReason
        startedAt
        finishedAt
      }
    }
  }
`;

/**
 * One translation, for the edit window at
 * /perception-models/:normSetId/translations/:translationId.
 *
 * Keyed on the parent as well as the id. This table carries no producerId — a
 * translation reaches its workspace through its norm set — so the caller
 * resolves the parent by (id, producerId) first and passes it in here. A
 * translation id typed from another workspace matches nothing and reads as
 * missing, which is what it is from that workspace's point of view.
 */
export const ProjectTranslationByIdQuery = gql`
  query ProjectTranslationByIdQuery($id: Int!, $projectId: Int!) {
    allPerceptionModelProjectTranslations(
      condition: { id: $id, perceptionModelProjectId: $projectId }
    ) {
      nodes {
        id
        name
        perceptionModelProjectId
        products
        demographic
        createdAt
        status
        statusReason
        startedAt
        finishedAt
      }
    }
  }
`;

/** One entry in the token vocabulary, from the aggregated view. */
export type TokenVocabularyRow = {
  kind: string;
  token: string;
  productCount: number;
};

/**
 * The token vocabulary for one job.
 *
 * The view does the grouping, so this is a plain select of 627 rows rather than
 * a `groupedAggregates` call — that would need the pg-aggregates plugin, which
 * nothing else here relies on. Server-side expansion of 1.2M product-token
 * pairs measured ~217 ms.
 */
export const TokenVocabularyQuery = gql`
  query TokenVocabularyQuery($jobId: Int!) {
    allPerceptionModelTokenVocabularies(
      condition: { perceptionModelJobId: $jobId }
      orderBy: PRODUCT_COUNT_DESC
    ) {
      nodes {
        kind
        token
        productCount
      }
    }
  }
`;

/**
 * Products carrying any of these tokens.
 *
 * Returns one row per product per matching token, so the caller intersects for
 * AND semantics. Scales with how common the chosen tokens are rather than with
 * the universe: the worst single token on job 5 covers 14,554 products (~350 kB),
 * against 21 MB to ship every token map to the browser.
 */
export const TokenMatchQuery = gql`
  query TokenMatchQuery($jobId: Int!, $tokens: [String!]) {
    allPerceptionModelTokens(
      filter: {
        perceptionModelJobId: { equalTo: $jobId }
        token: { in: $tokens }
      }
    ) {
      nodes {
        kind
        token
        productName
      }
    }
  }
`;

/** Workspace-owned products, the anchor candidate pool. */ export const WorkspaceProductsQuery = gql`
  query WorkspaceProductsQuery($producerId: Int!) {
    allProducts(condition: { producerId: $producerId }, orderBy: NAME_ASC) {
      nodes {
        id
        name
        brand
        category: productCategoryByCategoryId {
          id
          name
        }
      }
    }
  }
`;

/**
 * One training run, for the model viewer at /perception-models/models/:jobId.
 *
 * Not scoped by producerId, and it cannot be: `perception_model_job` has no
 * such column. A model is trained across the whole review corpus rather than
 * for one workspace, which is exactly why a norm set has to record which one it
 * was built on. Nothing workspace-specific is selected here — the row is run
 * metadata, and the products behind it are counted rather than listed.
 *
 * `params` carries `demo_levels`, which is the only record of what the model
 * was trained on. A translation naming a level that is not in it fails in R
 * rather than rendering something plausible, so it is the most useful thing on
 * the page.
 */
export const PerceptionModelJobByIdQuery = gql`
  query PerceptionModelJobByIdQuery($id: Int!) {
    allPerceptionModelJobs(condition: { id: $id }) {
      nodes {
        id
        versionId
        universe
        pullDate
        params
        status
        statusReason
        startedAt
        finishedAt
        isDeployed
        perceptionModelProductLookupsByPerceptionModelJobId {
          totalCount
        }
      }
    }
  }
`;

/** Norm sets built on one model, so the viewer can say what depends on it. */
export const NormSetsForJobQuery = gql`
  query NormSetsForJobQuery($jobId: Int!, $producerId: Int!) {
    allPerceptionModelProjects(
      condition: { perceptionModelJobId: $jobId, producerId: $producerId }
      orderBy: CREATED_DATE_DESC
    ) {
      nodes {
        id
        name
        perceptionModelJobId
        producerId
        anchors
        normSet
        createdDate
      }
    }
  }
`;
